import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Title,
  Text,
  PasswordInput,
  Button,
  Stack,
  Alert,
  Flex,
  Image,
  Loader,
  Center,
  Progress,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { AlertCircle, Lock, Check, ArrowLeft } from 'react-feather';
import { authService } from '../../services/api/auth';
import classes from './LoginPage.module.css';
import medleyLogoWhite from '../../assets/medley-logo-white.png';

interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

// Password strength calculator (same as AcceptInvitationPage)
function getPasswordStrength(password: string): number {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 15;
  if (/[a-z]/.test(password)) strength += 15;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 15;
  return Math.min(100, strength);
}

function getStrengthColor(strength: number): string {
  if (strength < 30) return 'red';
  if (strength < 60) return 'yellow';
  if (strength < 80) return 'blue';
  return 'green';
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<ResetPasswordFormValues>({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validate: {
      password: value => {
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (getPasswordStrength(value) < 60) return 'Password is too weak';
        return null;
      },
      confirmPassword: (value, values) =>
        value !== values.password ? 'Passwords do not match' : null,
    },
  });

  const passwordStrength = getPasswordStrength(form.values.password);

  // Validate reset token on mount
  useEffect(() => {
    async function validateToken() {
      if (!token) {
        setValidationError('Invalid password reset link');
        setIsValidating(false);
        return;
      }

      try {
        const data = await authService.validateResetToken(token);
        if (data.valid) {
          setUserEmail(data.email);
        } else {
          setValidationError('This password reset link is invalid or has expired');
        }
      } catch (error) {
        setValidationError(
          error instanceof Error
            ? error.message
            : 'This password reset link is invalid or has expired'
        );
      } finally {
        setIsValidating(false);
      }
    }

    validateToken();
  }, [token]);

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    if (!token) return;

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await authService.resetPassword(token, values.password);
      setIsSuccess(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to reset password'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <Box className={classes.wrapper}>
        <div className={classes.pattern} />
        <Center h="100vh">
          <Stack align="center" gap="md">
            <Loader size="lg" color="medleyGreen" />
            <Text c="dimmed">Validating reset link...</Text>
          </Stack>
        </Center>
      </Box>
    );
  }

  // Invalid token state
  if (validationError || !userEmail) {
    return (
      <Box className={classes.wrapper}>
        <div className={classes.pattern} />
        <Flex className={classes.container} justify="center">
          <Paper className={classes.formCard} p={40} radius="lg" shadow="xl" maw={420}>
            <Stack align="center" gap="md">
              <AlertCircle size={48} color="#D64545" />
              <Title order={3} ta="center">
                Invalid Reset Link
              </Title>
              <Text c="dimmed" ta="center">
                {validationError || 'This password reset link is invalid or has expired.'}
              </Text>
              <Stack gap="xs" w="100%" mt="md">
                <Button
                  variant="light"
                  color="medleyGreen"
                  component={Link}
                  to="/forgot-password"
                  fullWidth
                >
                  Request new reset link
                </Button>
                <Button
                  variant="subtle"
                  color="gray"
                  component={Link}
                  to="/login"
                  fullWidth
                  leftSection={<ArrowLeft size={16} />}
                >
                  Back to login
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Flex>
      </Box>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <Box className={classes.wrapper}>
        <div className={classes.pattern} />
        <Flex className={classes.container} justify="center">
          <Paper className={classes.formCard} p={40} radius="lg" shadow="xl" maw={420}>
            <Stack align="center" gap="md">
              <Box
                p="md"
                style={{
                  backgroundColor: 'var(--mantine-color-green-0)',
                  borderRadius: '50%',
                }}
              >
                <Check size={32} color="var(--mantine-color-green-6)" />
              </Box>
              <Title order={3} ta="center">
                Password reset successful
              </Title>
              <Text c="dimmed" ta="center">
                Your password has been reset successfully. You can now sign in
                with your new password.
              </Text>
              <Button
                color="medleyGreen"
                component={Link}
                to="/login"
                fullWidth
                mt="md"
              >
                Sign in
              </Button>
            </Stack>
          </Paper>
        </Flex>
      </Box>
    );
  }

  return (
    <Box className={classes.wrapper}>
      <div className={classes.pattern} />

      <Flex className={classes.container}>
        {/* Left Side - Branding */}
        <Box className={classes.branding} visibleFrom="md">
          <Box
            mb={48}
            p="sm"
            style={{
              backgroundColor: '#2D4739',
              borderRadius: 8,
              display: 'inline-block',
            }}
          >
            <Image src={medleyLogoWhite} alt="Medley" h={28} w="auto" fit="contain" />
          </Box>

          <Title order={1} className={classes.headline}>
            Create a new
            <br />
            password
          </Title>

          <Text size="lg" c="dimmed" mt="lg" maw={400}>
            Enter a new password for your account. Make sure it's strong and
            unique to keep your account secure.
          </Text>

          <Stack mt={48} gap="lg">
            <Stack gap={4}>
              <Text size="sm" fw={500} c="dimmed">
                Resetting password for:
              </Text>
              <Text fw={600}>{userEmail}</Text>
            </Stack>
          </Stack>
        </Box>

        {/* Right Side - Form */}
        <Box className={classes.formSection}>
          <Paper className={classes.formCard} p={40} radius="lg" shadow="xl">
            <Title order={2} mb={4}>
              Set new password
            </Title>

            <Text c="dimmed" size="sm" mb="xl">
              Create a secure password for your account
            </Text>

            {/* Mobile only - show email */}
            <Box hiddenFrom="md" mb="lg" p="md" bg="gray.0" style={{ borderRadius: 8 }}>
              <Text size="sm" c="dimmed">
                Resetting password for:
              </Text>
              <Text fw={600} size="sm">
                {userEmail}
              </Text>
            </Box>

            {submitError && (
              <Alert
                icon={<AlertCircle size={16} />}
                title="Error"
                color="red"
                mb="md"
                onClose={() => setSubmitError(null)}
                withCloseButton
              >
                {submitError}
              </Alert>
            )}

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                <Box>
                  <PasswordInput
                    label="New Password"
                    placeholder="Create a password"
                    leftSection={<Lock size={18} />}
                    size="md"
                    {...form.getInputProps('password')}
                  />
                  {form.values.password && (
                    <Box mt="xs">
                      <Progress
                        value={passwordStrength}
                        color={getStrengthColor(passwordStrength)}
                        size="sm"
                      />
                      <Text size="xs" c="dimmed" mt={4}>
                        {passwordStrength < 30 && 'Weak password'}
                        {passwordStrength >= 30 && passwordStrength < 60 && 'Fair password'}
                        {passwordStrength >= 60 && passwordStrength < 80 && 'Good password'}
                        {passwordStrength >= 80 && 'Strong password'}
                      </Text>
                    </Box>
                  )}
                </Box>

                <PasswordInput
                  label="Confirm Password"
                  placeholder="Confirm your password"
                  leftSection={<Check size={18} />}
                  size="md"
                  {...form.getInputProps('confirmPassword')}
                />

                <Text size="xs" c="dimmed">
                  Password must be at least 8 characters and include a mix of letters,
                  numbers, and symbols.
                </Text>

                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  mt="sm"
                  loading={isSubmitting}
                >
                  Reset Password
                </Button>
              </Stack>
            </form>
          </Paper>
        </Box>
      </Flex>
    </Box>
  );
}

export default ResetPasswordPage;
